import type { WidgetTheme } from "../types";

function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

export function generateStyles(theme: WidgetTheme): string {
  const c = theme.primaryColor;
  const ch = adjustColor(c, -10);
  return `
:host {
  --oc-primary: ${c}; --oc-primary-hover: ${ch}; --oc-bg: #ffffff;
  --oc-text: #1e293b; --oc-text-secondary: #64748b; --oc-border: #e5edf5;
  --oc-radius: 12px; --oc-shadow: 0 25px 50px -12px rgba(15,23,42,0.2);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
}
* { box-sizing: border-box; margin: 0; padding: 0; }
@keyframes oc-ping { 75%, 100% { transform: scale(1.2); opacity: 0; } }
@keyframes oc-fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes oc-wave { 0%, 100% { height: 8px; } 50% { height: 32px; } }

.oc-container { position: fixed; z-index: 2147483646; display: flex; flex-direction: column; align-items: flex-end; gap: 12px; }
.oc-container.bottom-right { bottom: 24px; right: 24px; }
.oc-container.bottom-left { bottom: 24px; left: 24px; align-items: flex-start; }
.oc-container.top-right { top: 24px; right: 24px; }
.oc-container.top-left { top: 24px; left: 24px; align-items: flex-start; }

.oc-fab { width: 56px; height: 56px; border-radius: 28px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; background: var(--oc-primary); box-shadow: 0 4px 16px rgba(0,0,0,0.25); position: relative; transition: transform 0.2s, box-shadow 0.2s; flex-shrink: 0; }
.oc-fab:hover { transform: scale(1.08); box-shadow: 0 6px 24px rgba(0,0,0,0.35); }
.oc-fab:active { transform: scale(0.96); }
.oc-fab.pulse::after { content: ""; position: absolute; inset: -4px; border-radius: 32px; border: 2px solid var(--oc-primary); animation: oc-ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite; }
/* FAB — dual-icon swap animation */
.oc-fab-icon { position: absolute; display: flex; align-items: center; justify-content: center; transition: opacity 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.oc-fab-icon svg { width: 24px; height: 24px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.oc-fab-icon-chat { opacity: 1; transform: scale(1) rotate(0deg); }
.oc-fab-icon-close { opacity: 0; transform: scale(0.5) rotate(-90deg); }
.oc-fab.oc-fab-close .oc-fab-icon-chat { opacity: 0; transform: scale(0.5) rotate(90deg); }
.oc-fab.oc-fab-close .oc-fab-icon-close { opacity: 1; transform: scale(1) rotate(0deg); }

.oc-panel { display: none; width: 400px; height: 580px; max-width: calc(100vw - 48px); max-height: 85vh; border-radius: var(--oc-radius); overflow: hidden; background: var(--oc-bg); box-shadow: var(--oc-shadow); border: 1px solid var(--oc-border); flex-direction: column; animation: oc-fadeIn 0.25s ease-out; }
.oc-panel.open { display: flex; }

.oc-panel-header { position: relative; padding: 20px; background: var(--oc-primary); color: #fff; }
.oc-panel-header-top { display: flex; align-items: flex-start; gap: 8px; }
.oc-panel-back { width: 28px; height: 28px; border: none; border-radius: 9999px; background: rgba(255,255,255,0.2); cursor: pointer; display: none; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; margin-top: -2px; }
.oc-panel-back:hover { background: rgba(255,255,255,0.35); }
.oc-panel-close { position: absolute; top: 16px; right: 16px; width: 28px; height: 28px; border: none; border-radius: 9999px; background: rgba(255,255,255,0.2); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #fff; line-height: 1; flex-shrink: 0; }
.oc-panel-close:hover { background: rgba(255,255,255,0.35); }
.oc-panel-title-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 8px; background: rgba(255,255,255,0.15); flex-shrink: 0; }
.oc-panel-title-row { display: flex; align-items: center; gap: 8px; flex: 1; }
.oc-panel-title { font-size: 20px; font-weight: 700; color: #fff; }
.oc-panel-subtitle { font-size: 14px; opacity: 0.92; margin-top: 8px; margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
.oc-subtitle-dot { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; flex-shrink: 0; }
.oc-panel-body { flex: 1; padding: 0; overflow-y: auto; }
.oc-panel-footer { padding: 12px 20px; border-top: 1px solid var(--oc-border); text-align: center; font-size: 11px; color: var(--oc-text-secondary); }

/* text-chat 布局 */
.tc-root{display:flex;flex-direction:column;height:100%;}
.tc-body{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:12px;background:#fafbfc;}
.tc-welcome-sub{font-size:13px;color:#999;text-align:center;padding:20px 0;}
#tc-messages{display:flex;flex-direction:column;gap:14px;}.tc-row{display:flex;gap:8px;align-items:flex-end;}
.tc-row.agent{justify-content:flex-start;}
.tc-row.user{justify-content:flex-end;}
.tc-av{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;font-size:12px;}
.tc-av.agent-av{background:var(--oc-primary);}
.tc-av.user-av{background:#d1d5db;}
.tc-bubble{max-width:78%;padding:10px 14px;border-radius:16px;font-size:13px;line-height:1.55;word-break:break-word;}
.tc-row.agent .tc-bubble{background:#fff;color:var(--oc-text);border:1px solid #eee;border-bottom-left-radius:4px;}
.tc-row.user .tc-bubble{background:var(--oc-primary);color:#fff;border-bottom-right-radius:4px;}
.tc-footer{flex-shrink:0;padding:10px 16px;border-top:1px solid var(--oc-border);background:#fff;}
.tc-row-inp{display:flex;align-items:center;gap:8px;}
.tc-input{flex:1;padding:10px 16px;border:1px solid #e5e7eb;border-radius:24px;font-size:14px;background:#f9fafb;color:var(--oc-text);outline:none;}
.tc-input:focus{border-color:var(--oc-primary);background:#fff;}
.tc-input::placeholder{color:#bbb;}
.tc-send{width:38px;height:38px;border:none;border-radius:50%;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.tc-send:hover{opacity:0.85;}
.tc-send:disabled{opacity:0.4;}

/* home 视图 */
.hm-rec-card{background:#fff;border:1px solid var(--oc-border);border-radius:14px;padding:16px;margin-bottom:12px;}
.hm-rec-name{font-size:15px;font-weight:600;color:var(--oc-text);}
.hm-rec-status{font-size:12px;color:#22c55e;}
.hm-btn-wide{display:flex;align-items:center;width:100%;padding:13px 14px;border:1px solid #e8ecf1;border-radius:12px;background:#f8fafd;cursor:pointer;margin-bottom:8px;font-size:14px;color:var(--oc-text);transition:all 0.15s;}
.hm-btn-wide:hover{background:#eef2ff;border-color:var(--oc-primary);}
.hm-im-section{margin-top:4px;}

/* voice/callback 面板内容居中 + 90% 宽 */
.oc-v-header{text-align:center;margin-bottom:16px;}
.oc-v-phone-row,.oc-v-code-row,.oc-v-start-btn,.oc-v-cancel-btn,.oc-v-hint,.oc-v-error{max-width:90%;margin-left:auto;margin-right:auto;}
.oc-v-start-btn,.oc-v-cancel-btn{display:block;width:90%;}
.oc-cb-phone-input,.oc-cb-input,.oc-cb-textarea,.oc-cb-submit,.oc-cb-resend{width:90%!important;max-width:90%!important;margin-left:auto!important;margin-right:auto!important;}
.oc-cb-submit{display:block!important;}

@media (max-width: 420px) { .oc-panel { width: calc(100vw - 32px); max-height: 70vh; } }
`;
}
